import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PhoneWrapper } from 'src/svg/phone-wrapper';
import { CustomNavigationItemType, GqlCustomNavigationItem } from 'types/graphql';

const MAX_PREVIEW_ITEMS = 6;

type CustomNavigationPreviewProps = {
  items: GqlCustomNavigationItem[];
  isNested?: boolean;
  onNavigateUp?: () => void;
  onSectionClick?: (sectionId: string) => void;
  title?: string;
};

export function CustomNavigationPreview({
  items,
  isNested = false,
  onNavigateUp,
  onSectionClick,
  title = 'Menu',
}: CustomNavigationPreviewProps): JSX.Element {
  const flags = useFlags();
  const showRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  const displayItems = items.slice(0, MAX_PREVIEW_ITEMS);
  const isBackButtonClickable = isNested && !!onNavigateUp;

  return (
    <Container>
      <PhoneContainer>
        <PhoneWrapper />
        <ContentContainer>
          {!showRedesign && (
            <TabBar>
              <CloseButton
                as={isBackButtonClickable ? 'button' : undefined}
                type={isBackButtonClickable ? 'button' : undefined}
                onClick={isBackButtonClickable ? onNavigateUp : undefined}
              >
                {isBackButtonClickable ? '‹' : '×'}
              </CloseButton>
              <Tab $isActive>{title}</Tab>
              <Tab>Categories</Tab>
              <Tab>Brands</Tab>
              <Tab>Store Info</Tab>
            </TabBar>
          )}

          <MenuList $hideTabBar={showRedesign}>
            {displayItems.map((item) => {
              const isSection = item.navigationType === CustomNavigationItemType.section;
              const isClickableSection = isSection && !!onSectionClick;

              return (
                <MenuItem
                  key={item.id}
                  as={isClickableSection ? 'button' : undefined}
                  type={isClickableSection ? 'button' : undefined}
                  onClick={isClickableSection ? () => onSectionClick(item.id) : undefined}
                  $isClickable={isClickableSection}
                >
                  <MenuItemLabel>{item.label}</MenuItemLabel>
                  {isSection && <ChevronIcon>›</ChevronIcon>}
                </MenuItem>
              );
            })}
          </MenuList>
        </ContentContainer>
      </PhoneContainer>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const PhoneContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  padding: 20px;
  position: relative;
`;

const ContentContainer = styled.div`
  left: 31px;
  // The phone frame (PhoneWrapper) is a fixed-size SVG (802px tall); this content is an absolutely
  // positioned overlay on top of it starting 60px down, so however many nav items render (or however
  // long their labels are) needs to stay within the remaining height or it visually spills past the
  // bottom of the phone frame. Scrolling the menu list itself (see MenuList's max-height/overflow-y
  // below) keeps the overlay within the frame regardless of item count — this container just needs to
  // not clip that scroll area.
  max-height: calc(802px - 60px - 20px);
  overflow: hidden;
  position: absolute;
  top: 60px;
  width: calc(100% - 62px);
`;

const TabBar = styled.div`
  align-items: center;
  display: flex;
`;

const CloseButton = styled.span`
  border-bottom: 1px solid #e8eaed;
  color: #000;
  cursor: pointer;
  flex: 0 1 20px;
  font-size: 20px;
  font-weight: 300;
  line-height: 1;
  min-width: 26px;
  padding: 8px 8px 6px 9px;
  text-align: center;

  &[type='button'] {
    appearance: none;
    background: none;
    border-left: none;
    border-right: none;
    border-top: none;
    font: inherit;
    margin: 0;
    width: 100%;
  }
`;

const Tab = styled.span<{ $isActive?: boolean }>`
  border-bottom: 1px solid ${({ $isActive }) => ($isActive ? '#0b99e6' : '#e8eaed')};
  color: ${({ $isActive }) => ($isActive ? '#000' : '#677882')};
  cursor: pointer;
  flex: 1 0 0px;
  font-size: 12px;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '400')};
  overflow: clip;
  padding: 8px 12px;
  position: relative;
  white-space: nowrap;
`;

const MenuList = styled.div<{ $hideTabBar?: boolean }>`
  display: flex;
  flex-direction: column;
  // Scrolls within the phone frame instead of pushing/overflowing past its bottom edge --
  // subtracts the TabBar's own height (~37px) from ContentContainer's max-height above.
  max-height: ${({ $hideTabBar }) => ($hideTabBar ? 'calc(802px - 60px - 20px)' : 'calc(802px - 60px - 20px - 37px)')};
  overflow-y: auto;
  padding: 8px 0;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MenuItem = styled.div<{ $isClickable?: boolean }>`
  align-items: center;
  border-bottom: 1px solid #f0f2f4;
  box-sizing: border-box;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 16px 20px;
  text-align: left;
  width: 100%;

  &[type='button'] {
    appearance: none;
    background: none;
    border-top: 0;
    border-left: 0;
    border-right: 0;
    font: inherit;
  }
`;

const MenuItemLabel = styled.span`
  color: #000;
  // Long labels wrap instead of overflowing past the phone frame's right edge or pushing the
  // chevron icon out of view.
  min-width: 0;
  overflow-wrap: break-word;
  font-size: 16px;
`;

const ChevronIcon = styled.span`
  color: #677882;
  font-size: 20px;
`;
